<template>
  <div>
    <!-- Main Scheduled Tasks List Modal -->
    <b-modal
      :model-value="visible"
      @update:model-value="$emit('update:visible', $event)"
      title="Scheduled Tasks"
      size="xl"
      hide-footer
      modal-class="scheduled-tasks-modal"
    >
      <template #title>
        Scheduled Tasks
        <modal-help topic="scheduled-tasks" />
      </template>
      <div class="st-toolbar">
        <b-button size="sm" class="st-btn-new" @click="openCreate">
          <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" style="margin-right:4px;vertical-align:-1px;"><path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/></svg>
          New Schedule
        </b-button>
        <b-button size="sm" variant="outline-secondary" class="st-btn-logs" @click="openAllLogs">
          <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor" style="margin-right:4px;vertical-align:-2px;"><path d="M5 8a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm4 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm3 1a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"/><path d="M2.5 0c-.166 0-.33.016-.487.048l.194.98A1.51 1.51 0 0 1 2.5 1h.458a.5.5 0 0 1 .49.404l.482 2.645A.5.5 0 0 1 3.44 4.5H0v1h3.44a1.5 1.5 0 0 0 1.47-1.206l.17-.932A2.5 2.5 0 0 1 7.5 1H13a2.5 2.5 0 0 1 2.45 2h.05v1h-.05A2.5 2.5 0 0 1 13 6H7.5a2.5 2.5 0 0 1-2.42-1.862l-.17.932A1.5 1.5 0 0 0 3.44 6.5H0v1h3.44a2.5 2.5 0 0 0 2.45-2.018l.482-2.645A1.5 1.5 0 0 0 7.85 1h-.35A1.5 1.5 0 0 0 6 2.5v1A1.5 1.5 0 0 0 7.5 5H13a1.5 1.5 0 0 0 1.5-1.5v-1A1.5 1.5 0 0 0 13 1H7.5a1.5 1.5 0 0 0-1.47 1.206z"/></svg>
          Run Logs
        </b-button>
      </div>

      <div v-if="loading" class="text-center py-4">
        <span class="text-muted">Loading schedules...</span>
      </div>
      <div v-else-if="schedules.length === 0" class="st-empty">
        <span class="text-muted">No scheduled tasks for this spreadsheet.</span>
      </div>
      <table v-else class="table table-sm st-table">
        <thead>
          <tr>
            <th>Title</th>
            <th>Action</th>
            <th>Schedule</th>
            <th>Last Run</th>
            <th>Next Run</th>
            <th>Runs</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="schedule in schedules" :key="schedule._id">
            <td class="st-cell-title">{{ schedule.title }}</td>
            <td><span class="st-action-badge" :class="'st-action-' + schedule.actionType">{{ actionLabel(schedule.actionType) }}</span></td>
            <td class="st-cell-schedule">{{ scheduleDescription(schedule) }}</td>
            <td class="st-cell-time">{{ schedule.lastRunAt ? formatTime(schedule.lastRunAt) : '—' }}</td>
            <td class="st-cell-time">{{ schedule.nextRunAt ? formatTime(schedule.nextRunAt) : '—' }}</td>
            <td class="st-cell-count">{{ schedule.runCount || 0 }}</td>
            <td>
              <span class="st-status-badge" :class="schedule.enabled ? 'st-status-active' : 'st-status-paused'">
                {{ schedule.enabled ? 'Active' : 'Paused' }}
              </span>
            </td>
            <td class="st-cell-actions">
              <b-button size="sm" variant="outline-light" class="st-edit-btn" @click="openEdit(schedule)">Edit</b-button>
              <b-button size="sm" variant="outline-secondary" class="st-logs-btn" @click="openScheduleLogs(schedule)">Logs</b-button>
              <b-button size="sm" variant="outline-danger" class="st-delete-btn" @click="confirmDelete(schedule)">
                <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/><path fill-rule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H5.5l1-1h3l1 1h2.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/></svg>
              </b-button>
            </td>
          </tr>
        </tbody>
      </table>
    </b-modal>

    <!-- Create/Edit Schedule Modal -->
    <b-modal
      v-model="formVisible"
      :title="editingSchedule ? 'Edit Scheduled Task' : 'New Scheduled Task'"
      size="lg"
      @ok="saveSchedule"
      :ok-disabled="!formValid || formSubmitting"
      :ok-title="formSubmitting ? 'Saving...' : 'Save'"
    >
      <template #title>
        {{ editingSchedule ? 'Edit Scheduled Task' : 'New Scheduled Task' }}
        <modal-help topic="scheduled-task-form" />
      </template>
      <b-container fluid>
        <b-form>
          <b-row>
            <b-form-group label="Task Title">
              <b-form-input v-model="form.title" placeholder="e.g. Nightly status update" />
            </b-form-group>
          </b-row>

          <hr />
          <div class="st-form-section-label">Action</div>

          <b-row>
            <b-form-group label="Action Type">
              <b-form-select v-model="form.actionType" :options="actionTypeOptions" size="sm" />
            </b-form-group>
          </b-row>

          <!-- Bulk Update values -->
          <template v-if="form.actionType === 'bulk_update'">
            <div class="st-form-subsection-label">Filter — select which records to update</div>
            <runtime-filter-builder
              v-if="formVisible && spreadSheet"
              :field-groups="fieldGroups"
              :initial-filters="form.filters || []"
              :initial-match-mode="form.filterMatchMode || 'all'"
              @filter-changed="onFormFilterChanged"
            />
            <div class="st-form-subsection-label mt-3">Values — only filled fields will be updated</div>
            <b-row v-for="col in editableColumns" :key="'sf-' + col.uniqueTitle">
              <b-form-group :label="col.title">
                <b-form-select
                  v-if="col.type === 'multiselect'"
                  class="mb-2"
                  :model-value="fieldOp(col)"
                  :options="multiselectOpOptions"
                  @update:model-value="setFieldOp(col.uniqueTitle, $event)"
                />
                <b-form-input
                  v-if="col.type === 'number'"
                  :model-value="form.values[col.uniqueTitle]"
                  @update:model-value="setFormValue(col.uniqueTitle, $event)"
                  type="number"
                  placeholder="leave empty to keep existing"
                />
                <b-form-input
                  v-else-if="col.type === 'dateTime'"
                  :model-value="form.values[col.uniqueTitle]"
                  @update:model-value="setFormValue(col.uniqueTitle, $event)"
                  type="datetime-local"
                  placeholder="leave empty to keep existing"
                />
                <b-form-input
                  v-else
                  :model-value="form.values[col.uniqueTitle]"
                  @update:model-value="setFormValue(col.uniqueTitle, $event)"
                  type="text"
                  :placeholder="col.type === 'multiselect' && fieldOp(col) !== 'replace' ? 'one value' : (col.type === 'multiselect' ? 'comma-separated values' : 'leave empty to keep existing')"
                />
              </b-form-group>
            </b-row>
            <b-row v-for="col in relationshipColumns" :key="'sfr-' + col.uniqueTitle">
              <b-form-group :label="col.title">
                <b-form-select
                  class="mb-2"
                  :model-value="fieldOp(col)"
                  :options="relationshipOpOptions"
                  @update:model-value="setFieldOp(col.uniqueTitle, $event)"
                />
                <b-form-input
                  :model-value="form.values[col.uniqueTitle]"
                  @update:model-value="setFormValue(col.uniqueTitle, $event)"
                  type="number"
                  placeholder="one record id"
                />
              </b-form-group>
            </b-row>
          </template>

          <!-- Bulk Delete filters -->
          <template v-if="form.actionType === 'bulk_delete'">
            <div class="st-form-subsection-label">Filter — select which records to delete</div>
            <runtime-filter-builder
              v-if="formVisible && spreadSheet"
              :field-groups="fieldGroups"
              :initial-filters="form.filters || []"
              :initial-match-mode="form.filterMatchMode || 'all'"
              @filter-changed="onFormFilterChanged"
            />
            <b-alert variant="warning" show class="mt-2" style="font-size:0.85rem;">
              Records matching the filter will be permanently deleted when this task runs.
            </b-alert>
          </template>

          <!-- Trigger Automation -->
          <template v-if="form.actionType === 'trigger_automation'">
            <b-row>
              <b-form-group label="Automation">
                <b-form-select v-model="form.automationUniqueTitle" :options="automationOptions" size="sm" />
              </b-form-group>
            </b-row>
            <b-row>
              <b-form-group label="Trigger Label">
                <b-form-select v-model="form.automationTrigger" :options="automationTriggerOptions" size="sm" />
              </b-form-group>
            </b-row>
            <div v-if="scheduleAutomationVariables.length" class="st-automation-variables">
              <div class="st-form-section-label">Automation variables</div>
              <p style="font-size:0.85rem;color:#9aa0a6;">These replace &#123;&#123;name&#125;&#125; placeholders when the schedule runs.</p>
              <b-form-group v-for="variable in scheduleAutomationVariables" :key="variable.variableName" :label="variable.variableName + ' (' + variable.location + ')'">
                <b-form-input v-model="form.automationVariables[variable.variableName]" size="sm" />
              </b-form-group>
            </div>
          </template>

          <template v-if="form.actionType === 'trigger_step_workflow'">
            <b-row>
              <b-form-group label="Step workflow">
                <b-form-select v-model="form.stepWorkflowUniqueTitle" :options="workflowOptions" size="sm" />
              </b-form-group>
            </b-row>
            <b-alert variant="info" show style="font-size:0.85rem;">
              Each matching record on this spreadsheet starts the workflow, up to 100 records. The workflow must use this spreadsheet.
            </b-alert>
          </template>

          <!-- Recalculate Formulas — no extra config needed -->
          <template v-if="form.actionType === 'recalculate_formulas'">
            <b-alert variant="info" show style="font-size:0.85rem;">
              All formula columns on this spreadsheet will be recalculated when this task runs.
            </b-alert>
          </template>

          <hr />
          <div class="st-form-section-label">Schedule</div>

          <b-row>
            <b-form-group label="Schedule Type">
              <b-form-select v-model="form.scheduleType" :options="scheduleTypeOptions" size="sm" />
            </b-form-group>
          </b-row>

          <!-- Once-off scheduling -->
          <template v-if="form.scheduleType === 'once'">
            <b-row>
              <b-form-group :label="'Run at (' + appZoneLabel + ')'">
                <b-form-input v-model="form.runAt" type="datetime-local" />
              </b-form-group>
            </b-row>
            <p v-if="onceBuilderClock" class="text-muted small">Where you are ({{ builderZoneLabel }}): {{ onceBuilderClock }}.</p>
          </template>

          <!-- Recurring scheduling -->
          <template v-if="form.scheduleType === 'recurring'">
            <b-row>
              <b-form-group label="Frequency">
                <b-form-select v-model="form.frequency" :options="frequencyOptions" size="sm" />
              </b-form-group>
            </b-row>

            <b-row v-if="showTimeField">
              <b-form-group :label="'Time of day (' + appZoneLabel + ')'">
                <b-form-input v-model="form.dailyTime" type="time" />
              </b-form-group>
            </b-row>
            <p v-if="showTimeField && builderClock" class="text-muted small">{{ form.dailyTime }} in {{ appZoneLabel }}. Where you are ({{ builderZoneLabel }}): {{ builderClock }}.</p>

            <b-row v-if="form.frequency === 'weekly'">
              <b-form-group label="Day of Week">
                <b-form-select v-model="form.weeklyDay" :options="weekdayOptions" size="sm" />
              </b-form-group>
            </b-row>

            <b-row v-if="form.frequency === 'monthly'">
              <b-form-group label="Day of Month">
                <b-form-input v-model.number="form.monthlyDay" type="number" min="1" max="31" />
              </b-form-group>
            </b-row>

            <b-row v-if="form.frequency === 'yearly'">
              <b-col cols="6">
                <b-form-group label="Month">
                  <b-form-select v-model.number="form.yearlyMonth" :options="monthOptions" size="sm" />
                </b-form-group>
              </b-col>
              <b-col cols="6">
                <b-form-group label="Day">
                  <b-form-input v-model.number="form.yearlyDay" type="number" min="1" max="31" />
                </b-form-group>
              </b-col>
            </b-row>

            <template v-if="form.frequency === 'custom'">
              <b-row>
                <b-col cols="6">
                  <b-form-group label="Every">
                    <b-form-input v-model.number="customIntervalValue" type="number" min="1" />
                  </b-form-group>
                </b-col>
                <b-col cols="6">
                  <b-form-group label="Unit">
                    <b-form-select v-model="customIntervalUnit" :options="customIntervalUnitOptions" size="sm" />
                  </b-form-group>
                </b-col>
              </b-row>
            </template>
          </template>

          <hr />
          <b-row>
            <b-form-group class="st-enabled-check">
              <b-form-checkbox v-model="form.enabled">Enabled</b-form-checkbox>
            </b-form-group>
          </b-row>

          <b-alert v-if="formError" variant="danger" show dismissible @dismissed="formError = null" class="mt-2">{{ formError }}</b-alert>
        </b-form>
      </b-container>
    </b-modal>

    <!-- Run Logs Modal -->
    <b-modal
      v-model="logsVisible"
      :title="logsTitle"
      size="xl"
      hide-footer
      modal-class="scheduled-tasks-logs-modal"
    >
      <template #title>
        {{ logsTitle }}
        <modal-help topic="scheduled-task-logs" />
      </template>
      <div v-if="logsLoading" class="text-center py-4">
        <span class="text-muted">Loading logs...</span>
      </div>
      <div v-else-if="logs.length === 0" class="st-empty">
        <span class="text-muted">No run logs found.</span>
      </div>
      <table v-else class="table table-sm st-table">
        <thead>
          <tr>
            <th>Task</th>
            <th>Action</th>
            <th>Started</th>
            <th>Finished</th>
            <th>Duration</th>
            <th>Records Affected</th>
            <th>Status</th>
            <th>Error</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="entry in logs" :key="entry._id">
            <td class="st-cell-title">{{ entry.scheduleTitle }}</td>
            <td><span class="st-action-badge" :class="'st-action-' + entry.actionType">{{ actionLabel(entry.actionType) }}</span></td>
            <td class="st-cell-time">{{ formatTime(entry.startedAt) }}</td>
            <td class="st-cell-time">{{ entry.finishedAt ? formatTime(entry.finishedAt) : '—' }}</td>
            <td class="st-cell-duration">{{ formatDuration(entry.startedAt, entry.finishedAt) }}</td>
            <td class="st-cell-count">{{ entry.recordsAffected }}</td>
            <td>
              <span class="st-status-badge" :class="entry.status === 'success' ? 'st-status-active' : 'st-status-error'">
                {{ entry.status }}
              </span>
            </td>
            <td class="st-cell-error">{{ entry.error || '' }}</td>
          </tr>
        </tbody>
      </table>
    </b-modal>

    <!-- Delete Confirmation -->
    <b-modal v-model="deleteVisible" title="Delete Scheduled Task" @ok="doDelete" ok-variant="danger" ok-title="Delete">
      <template #title>
        Delete Scheduled Task
        <modal-help topic="delete-scheduled-task" />
      </template>
      <p>Are you sure you want to delete the scheduled task <strong>{{ deleteTarget?.title }}</strong>? All run logs for this task will also be deleted.</p>
    </b-modal>
  </div>
</template>

<script>
import RuntimeFilterBuilder from './RuntimeFilterBuilder.vue'
import { automationVariableNames, variableLocationLabel } from '../../../shared/automation-integration.js'
import { browserTimeZone, clockInZone, formatInstant, normalizeTimeZone, timeZoneLabel, toDateTimeLocal, utcFromWallTime } from '../../../shared/app-timezone'
import { onDesignerChange, raiseDesignerWarning } from '../lib/designer-sync.js'

export default {
  components: { RuntimeFilterBuilder },
  props: {
    visible: { type: Boolean, default: false },
    spreadSheetUniqueTitle: { type: String, default: '' },
    spreadSheet: { type: Object, default: null },
    fieldGroups: { type: Array, default: () => [] },
    applicationUniqueTitle: { type: String, default: '' },
    automations: { type: Array, default: () => [] },
    workflows: { type: Array, default: () => [] }
  },
  emits: ['update:visible'],
  data () {
    return {
      schedules: [],
      appTimeZone: 'UTC',
      loading: false,
      formVisible: false,
      editingSchedule: null,
      form: this.freshForm(),
      formSubmitting: false,
      formError: null,
      customIntervalValue: 1,
      customIntervalUnit: 'days',
      logsVisible: false,
      logsTitle: 'Run Logs',
      logs: [],
      logsLoading: false,
      deleteVisible: false,
      deleteTarget: null,
      actionTypeOptions: [
        { value: 'bulk_update', text: 'Bulk Update' },
        { value: 'bulk_delete', text: 'Bulk Delete' },
        { value: 'recalculate_formulas', text: 'Recalculate Formulas' },
        { value: 'trigger_automation', text: 'Trigger Automation' },
        { value: 'trigger_step_workflow', text: 'Trigger Step Workflow' }
      ],
      scheduleTypeOptions: [
        { value: 'once', text: 'Run Once' },
        { value: 'recurring', text: 'Recurring' }
      ],
      frequencyOptions: [
        { value: 'every_minute', text: 'Every Minute' },
        { value: 'hourly', text: 'Hourly' },
        { value: 'daily', text: 'Daily' },
        { value: 'weekly', text: 'Weekly' },
        { value: 'monthly', text: 'Monthly' },
        { value: 'yearly', text: 'Yearly' },
        { value: 'custom', text: 'Custom Interval' }
      ],
      weekdayOptions: [
        { value: 0, text: 'Sunday' },
        { value: 1, text: 'Monday' },
        { value: 2, text: 'Tuesday' },
        { value: 3, text: 'Wednesday' },
        { value: 4, text: 'Thursday' },
        { value: 5, text: 'Friday' },
        { value: 6, text: 'Saturday' }
      ],
      monthOptions: [
        { value: 0, text: 'January' }, { value: 1, text: 'February' },
        { value: 2, text: 'March' }, { value: 3, text: 'April' },
        { value: 4, text: 'May' }, { value: 5, text: 'June' },
        { value: 6, text: 'July' }, { value: 7, text: 'August' },
        { value: 8, text: 'September' }, { value: 9, text: 'October' },
        { value: 10, text: 'November' }, { value: 11, text: 'December' }
      ],
      customIntervalUnitOptions: [
        { value: 'minutes', text: 'Minutes' },
        { value: 'hours', text: 'Hours' },
        { value: 'days', text: 'Days' },
        { value: 'weeks', text: 'Weeks' },
        { value: 'months', text: 'Months' }
      ],
      automationTriggerOptions: [
        { value: 'scheduled', text: 'Scheduled' },
        { value: 'manual', text: 'Manual' },
        { value: 'form_submission', text: 'Form Submission' }
      ]
    }
  },
  computed: {
    editableColumns () {
      if (!this.spreadSheet || !this.spreadSheet.columns) return []
      return this.spreadSheet.columns.filter(col => col.type !== 'formula' && col.type !== 'relationship' && col.type !== 'sequence')
    },
    relationshipColumns () {
      if (!this.spreadSheet || !this.spreadSheet.columns) return []
      return this.spreadSheet.columns.filter(col => col.type === 'relationship')
    },
    multiselectOpOptions () {
      return [
        { value: 'replace', text: 'Replace the values' },
        { value: 'append', text: 'Append one value' },
        { value: 'remove', text: 'Remove one value' }
      ]
    },
    relationshipOpOptions () {
      return [
        { value: '', text: 'Leave the links unchanged' },
        { value: 'append', text: 'Append one record' },
        { value: 'remove', text: 'Remove one record' }
      ]
    },
    automationOptions () {
      return [
        { value: null, text: '— select an automation —' },
        ...this.automations.map(s => ({ value: s.uniqueTitle, text: s.title || s.uniqueTitle }))
      ]
    },
    workflowOptions () {
      const matches = (this.workflows || []).filter(workflow => workflow.spreadSheetUniqueTitle === this.spreadSheetUniqueTitle)
      return [
        { value: null, text: '— select a workflow —' },
        ...matches.map(workflow => ({ value: workflow.uniqueTitle, text: workflow.title || workflow.uniqueTitle }))
      ]
    },
    showTimeField () {
      return ['daily', 'weekly', 'monthly', 'yearly'].includes(this.form.frequency)
    },
    formValid () {
      if (!this.form.title || !this.form.title.trim()) return false
      if (!this.form.actionType) return false
      if (this.form.scheduleType === 'once' && !this.form.runAt) return false
      if (this.form.scheduleType === 'recurring' && !this.form.frequency) return false
      if (this.form.actionType === 'trigger_automation' && !this.form.automationUniqueTitle) return false
      if (this.form.actionType === 'trigger_step_workflow' && !this.form.stepWorkflowUniqueTitle) return false
      return true
    },
    appZoneLabel () {
      return timeZoneLabel(this.appTimeZone)
    },
    builderZoneLabel () {
      return timeZoneLabel(browserTimeZone())
    },
    builderClock () {
      return clockInZone(this.form && this.form.dailyTime, this.appTimeZone, browserTimeZone())
    },
    onceBuilderClock () {
      if (!this.form || !this.form.runAt) return ''
      const instant = utcFromWallTime(this.form.runAt, this.appTimeZone)
      return instant ? formatInstant(instant, browserTimeZone()) : ''
    },
    scheduleAutomationVariables () {
      const automation = (this.automations || []).find(item => item.uniqueTitle === this.form.automationUniqueTitle)
      if (!automation) return []
      return automationVariableNames(automation).map(item => ({
        variableName: item.variableName,
        location: variableLocationLabel(item.location)
      }))
    }
  },
  watch: {
    visible (val) {
      if (val) {
        this.loadAppTimeZone()
        this.loadSchedules()
      }
    },
    'form.automationUniqueTitle' () {
      this.syncAutomationVariables()
    },
    automations () {
      this.syncAutomationVariables()
    }
  },
  mounted () {
    this._stopDesignerSync = onDesignerChange((change) => this.applyDesignerChange(change))
  },
  beforeUnmount () {
    if (this._stopDesignerSync) this._stopDesignerSync()
  },
  methods: {
    scheduleDraftOpen () {
      return !!(this.editingSchedule || (this.form && String(this.form.title || '').trim()) || this.deleteVisible)
    },
    async applyDesignerChange (change) {
      const keys = change && change.keys || []
      const hits = keys.includes('schedules') || keys.includes('application') || keys.some((key) => key.startsWith('schedule:'))
      if (!hits || !this.visible) return
      if (this.scheduleDraftOpen()) {
        raiseDesignerWarning(change, 'this scheduled task')
        return
      }
      await this.loadSchedules()
    },
    syncAutomationVariables () {
      if (!this.form) return
      const current = this.form.automationVariables || {}
      const next = {}
      for (const item of this.scheduleAutomationVariables) {
        next[item.variableName] = current[item.variableName] || ''
      }
      this.form.automationVariables = next
    },
    freshForm () {
      return {
        title: '',
        enabled: true,
        scheduleType: 'once',
        runAt: '',
        frequency: 'daily',
        dailyTime: '09:00',
        weeklyDay: 1,
        monthlyDay: 1,
        yearlyMonth: 0,
        yearlyDay: 1,
        customInterval: null,
        actionType: 'bulk_update',
        filters: [],
        filterMatchMode: 'all',
        values: {},
        fieldOps: {},
        automationUniqueTitle: null,
        automationTrigger: 'scheduled',
        automationVariables: {},
        stepWorkflowUniqueTitle: null
      }
    },
    async loadAppTimeZone () {
      try {
        const response = await this.$http.get(`${VUE_APP_PROXY_SERVER_URL}/settings`, {
          headers: { 'application-unique-title': this.applicationUniqueTitle }
        })
        this.appTimeZone = normalizeTimeZone(response.data && response.data.timeZone)
      } catch (err) {
        this.appTimeZone = 'UTC'
      }
    },
    async loadSchedules () {
      this.loading = true
      try {
        const response = await this.$http.get(
          `${VUE_APP_PROXY_SERVER_URL}/schedules/spreadsheet/${this.spreadSheetUniqueTitle}`,
          { headers: { 'application-unique-title': this.applicationUniqueTitle } }
        )
        this.schedules = response.data
      } catch (err) {
        console.error('Failed to load schedules:', err)
        this.schedules = []
      }
      this.loading = false
    },
    openCreate () {
      this.editingSchedule = null
      this.form = this.freshForm()
      this.customIntervalValue = 1
      this.customIntervalUnit = 'days'
      this.formError = null
      this.formSubmitting = false
      this.formVisible = true
    },
    openEdit (schedule) {
      this.editingSchedule = schedule
      this.form = {
        title: schedule.title,
        enabled: schedule.enabled,
        scheduleType: schedule.scheduleType || 'once',
        runAt: schedule.runAt ? toDateTimeLocal(schedule.runAt, this.appTimeZone) : '',
        frequency: schedule.frequency || 'daily',
        dailyTime: schedule.dailyTime || '09:00',
        weeklyDay: schedule.weeklyDay != null ? schedule.weeklyDay : 1,
        monthlyDay: schedule.monthlyDay || 1,
        yearlyMonth: schedule.yearlyMonth != null ? schedule.yearlyMonth : 0,
        yearlyDay: schedule.yearlyDay || 1,
        customInterval: schedule.customInterval || null,
        actionType: schedule.actionType || 'bulk_update',
        filters: schedule.filters || [],
        filterMatchMode: schedule.filterMatchMode || 'all',
        values: { ...(schedule.values || {}) },
        fieldOps: {},
        automationUniqueTitle: schedule.automationUniqueTitle || null,
        automationTrigger: schedule.automationTrigger || 'scheduled',
        automationVariables: { ...(schedule.automationVariables || {}) },
        stepWorkflowUniqueTitle: schedule.stepWorkflowUniqueTitle || null
      }
      for (const change of schedule.changes || []) {
        if (!change || !change.column) continue
        this.form.fieldOps[change.column] = change.op
        this.form.values[change.column] = change.value
      }
      if (schedule.customInterval) {
        this.customIntervalValue = schedule.customInterval.value || 1
        this.customIntervalUnit = schedule.customInterval.unit || 'days'
      } else {
        this.customIntervalValue = 1
        this.customIntervalUnit = 'days'
      }
      this.formError = null
      this.formSubmitting = false
      this.formVisible = true
    },
    async saveSchedule (event) {
      event.preventDefault()

      if (!this.formValid) return

      this.formSubmitting = true
      this.formError = null

      const data = {
        ...this.form,
        spreadSheetUniqueTitle: this.spreadSheetUniqueTitle,
        values: { ...(this.form.values || {}) },
        changes: []
      }
      delete data.fieldOps

      const changeColumns = this.editableColumns.concat(this.relationshipColumns)

      for (const col of changeColumns) {
        const op = this.fieldOp(col)
        if (op !== 'append' && op !== 'remove') continue
        const value = data.values[col.uniqueTitle]
        if (value == null || value === '') continue
        data.changes.push({ column: col.uniqueTitle, op, value })
        delete data.values[col.uniqueTitle]
      }

      if (data.frequency === 'custom') {
        data.customInterval = { value: this.customIntervalValue, unit: this.customIntervalUnit }
      }

      if (data.runAt && data.scheduleType === 'once') {
        data.runAt = utcFromWallTime(data.runAt, this.appTimeZone)
      }

      try {
        if (this.editingSchedule) {
          await this.$http.put(
            `${VUE_APP_PROXY_SERVER_URL}/schedules/${this.editingSchedule._id}`,
            data,
            { headers: { 'application-unique-title': this.applicationUniqueTitle } }
          )
        } else {
          await this.$http.post(
            `${VUE_APP_PROXY_SERVER_URL}/schedules/`,
            data,
            { headers: { 'application-unique-title': this.applicationUniqueTitle } }
          )
        }
        this.formVisible = false
        await this.loadSchedules()
      } catch (err) {
        const msg = err?.response?.data || err?.message || 'Save failed'
        this.formError = typeof msg === 'string' ? msg : JSON.stringify(msg)
      }

      this.formSubmitting = false
    },
    onFormFilterChanged (filterEvent) {
      this.form.filters = filterEvent.filters || []
      this.form.filterMatchMode = filterEvent.matchMode || 'all'
    },
    setFormValue (key, value) {
      this.form.values = { ...this.form.values, [key]: value }
    },
    fieldOp (col) {
      if (!col) return 'replace'
      if (this.form.fieldOps && this.form.fieldOps[col.uniqueTitle]) return this.form.fieldOps[col.uniqueTitle]
      return col.type === 'relationship' ? '' : 'replace'
    },
    setFieldOp (key, value) {
      this.form.fieldOps = { ...(this.form.fieldOps || {}), [key]: value }
      if (value === 'append' || value === 'remove') this.setFormValue(key, '')
    },
    confirmDelete (schedule) {
      this.deleteTarget = schedule
      this.deleteVisible = true
    },
    async doDelete () {
      if (!this.deleteTarget) return
      try {
        await this.$http.delete(
          `${VUE_APP_PROXY_SERVER_URL}/schedules/${this.deleteTarget._id}`,
          { headers: { 'application-unique-title': this.applicationUniqueTitle } }
        )
        await this.loadSchedules()
      } catch (err) {
        console.error('Failed to delete schedule:', err)
      }
      this.deleteTarget = null
    },
    async openAllLogs () {
      this.logsTitle = 'Run Logs — All Tasks'
      this.logsLoading = true
      this.logsVisible = true
      try {
        const response = await this.$http.get(
          `${VUE_APP_PROXY_SERVER_URL}/schedule-logs/spreadsheet/${this.spreadSheetUniqueTitle}`,
          { headers: { 'application-unique-title': this.applicationUniqueTitle } }
        )
        this.logs = response.data
      } catch (err) {
        console.error('Failed to load logs:', err)
        this.logs = []
      }
      this.logsLoading = false
    },
    async openScheduleLogs (schedule) {
      this.logsTitle = `Run Logs — ${schedule.title}`
      this.logsLoading = true
      this.logsVisible = true
      try {
        const response = await this.$http.get(
          `${VUE_APP_PROXY_SERVER_URL}/schedules/${schedule._id}/logs`,
          { headers: { 'application-unique-title': this.applicationUniqueTitle } }
        )
        this.logs = response.data
      } catch (err) {
        console.error('Failed to load logs:', err)
        this.logs = []
      }
      this.logsLoading = false
    },
    actionLabel (actionType) {
      const labels = {
        bulk_update: 'Bulk Update',
        bulk_delete: 'Bulk Delete',
        recalculate_formulas: 'Recalc Formulas',
        trigger_automation: 'Trigger Automation',
        trigger_step_workflow: 'Trigger Step Workflow'
      }
      return labels[actionType] || actionType
    },
    scheduleDescription (schedule) {
      if (schedule.scheduleType === 'once') {
        return schedule.runAt ? `Once at ${this.formatTime(schedule.runAt)}` : 'Once (no time set)'
      }
      const freq = schedule.frequency
      if (freq === 'every_minute') return 'Every minute'
      if (freq === 'hourly') return 'Hourly'
      if (freq === 'daily') return `Daily at ${schedule.dailyTime || '—'} ${this.appTimeZone}`
      if (freq === 'weekly') return `Weekly on ${this.weekdayOptions.find(o => o.value === schedule.weeklyDay)?.text || '—'} at ${schedule.dailyTime || '—'}`
      if (freq === 'monthly') return `Monthly on day ${schedule.monthlyDay || '—'} at ${schedule.dailyTime || '—'}`
      if (freq === 'yearly') return `Yearly on ${this.monthOptions.find(o => o.value === schedule.yearlyMonth)?.text || '—'} ${schedule.yearlyDay || '—'}`
      if (freq === 'custom' && schedule.customInterval) {
        return `Every ${schedule.customInterval.value} ${schedule.customInterval.unit}`
      }
      return 'Unknown'
    },
    formatTime (dateStr) {
      if (!dateStr) return '—'
      const app = formatInstant(dateStr, this.appTimeZone)
      const here = formatInstant(dateStr, browserTimeZone())
      if (!app) return dateStr
      if (app === here) return `${app} (${this.appTimeZone})`
      return `${app} (${this.appTimeZone}); ${here} where you are`
    },
    formatDuration (startStr, endStr) {
      if (!startStr || !endStr) return '—'
      try {
        const ms = new Date(endStr) - new Date(startStr)
        if (ms < 1000) return `${ms}ms`
        if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
        return `${(ms / 60000).toFixed(1)}m`
      } catch {
        return '—'
      }
    },
    toLocalDatetime (dateStr) {
      if (!dateStr) return ''
      try {
        const d = new Date(dateStr)
        if (isNaN(d.getTime())) return ''
        const pad = (n) => String(n).padStart(2, '0')
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
      } catch {
        return ''
      }
    }
  }
}
</script>

<style scoped>
.st-toolbar {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.st-btn-new {
  font-size: 0.8rem;
  font-weight: 600;
  padding: 4px 14px;
  background-color: #9ad12b;
  border-color: #9ad12b;
  color: #1a1a17;
}
.st-btn-new:hover {
  background-color: #b0e040;
  border-color: #b0e040;
  color: #1a1a17;
}

.st-btn-logs {
  font-size: 0.8rem;
  padding: 4px 14px;
}

.st-empty {
  text-align: center;
  padding: 32px 16px;
}

.st-table {
  font-size: 0.85rem;
}

.st-cell-title {
  font-weight: 600;
  color: #e8e8e6;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.st-cell-time {
  font-size: 0.78rem;
  color: #999;
  white-space: nowrap;
}

.st-cell-duration {
  font-size: 0.78rem;
  color: #999;
  white-space: nowrap;
}

.st-cell-count {
  text-align: center;
  font-weight: 600;
}

.st-cell-schedule {
  font-size: 0.82rem;
  color: #b0b0ae;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.st-cell-error {
  font-size: 0.78rem;
  color: #e57373;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.st-cell-actions {
  display: flex;
  gap: 4px;
  white-space: nowrap;
}

.st-edit-btn, .st-logs-btn, .st-delete-btn {
  font-size: 0.75rem;
  padding: 2px 8px;
}

.st-action-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.st-action-bulk_update { background-color: rgba(77, 171, 247, 0.15); color: #4dabf7; }
.st-action-bulk_delete { background-color: rgba(229, 115, 115, 0.15); color: #e57373; }
.st-action-recalculate_formulas { background-color: rgba(154, 209, 43, 0.15); color: #9ad12b; }
.st-action-trigger_automation { background-color: rgba(206, 147, 216, 0.15); color: #ce93d8; }
.st-action-trigger_step_workflow { background-color: rgba(154, 209, 43, 0.15); color: #9ad12b; }

.st-status-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
}

.st-status-active { background-color: rgba(154, 209, 43, 0.15); color: #9ad12b; }
.st-status-paused { background-color: rgba(150, 150, 150, 0.15); color: #999; }
.st-status-error { background-color: rgba(229, 115, 115, 0.15); color: #e57373; }

.st-form-section-label {
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #888;
  margin-bottom: 8px;
}

.st-form-subsection-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #888;
  margin-bottom: 6px;
  margin-top: 8px;
}

.st-enabled-check :deep(.form-check-label) {
  color: #e8e8e6;
  font-weight: 600;
}
</style>

<style>
.scheduled-tasks-modal .modal-content,
.scheduled-tasks-logs-modal .modal-content {
  background-color: #1e1e1b;
  color: #e8e8e6;
  border-color: #474842;
}

.scheduled-tasks-modal .modal-header,
.scheduled-tasks-logs-modal .modal-header {
  border-color: #474842;
}

.scheduled-tasks-modal .modal-footer,
.scheduled-tasks-logs-modal .modal-footer {
  border-color: #474842;
}

.scheduled-tasks-modal .st-table,
.scheduled-tasks-logs-modal .st-table {
  --bs-table-bg: #1e1e1b;
  --bs-table-color: #e8e8e6;
  --bs-table-border-color: #474842;
  --bs-table-hover-bg: #2a2a27;
  --bs-table-hover-color: #e8e8e6;
  color: #e8e8e6;
  border-color: #474842;
}

.scheduled-tasks-modal .st-table th,
.scheduled-tasks-logs-modal .st-table th {
  background-color: #242421;
  color: #c8c8c6;
  font-weight: 600;
  font-size: 0.78rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border-color: #474842;
  padding: 8px 10px;
}

.scheduled-tasks-modal .st-table td,
.scheduled-tasks-logs-modal .st-table td {
  border-color: #474842;
  padding: 7px 10px;
  vertical-align: middle;
}

.scheduled-tasks-modal .st-table tbody tr:hover td,
.scheduled-tasks-logs-modal .st-table tbody tr:hover td {
  background-color: #2a2a27;
}
</style>
